import Decimal from 'decimal.js'
import type { Article, Client } from '../../db/modele'
import { EMAIL } from '../clients'
import { nouvelId } from '../id'
import { centimesVersEuros, formatEuro, lireNombre, prixVenteDepuisAchat } from '../money'
import { normaliser } from '../recherche'
import { normaliserUnite } from '../unites'
import type { Cellule, Feuille } from './lecture'

// Assistant d'import : en-tête, mapping des colonnes, contrôle des lignes,
// doublons et plan d'écriture. Code pur (l'écriture locale est dans appliquer.ts).

export type TypeImport = 'articles' | 'clients'
export type ContenuArticle = Omit<Article, 'id' | 'version' | 'updatedAt'>
export type ContenuClient = Omit<Client, 'id' | 'version' | 'updatedAt'>
export type Contenus = { articles: ContenuArticle; clients: ContenuClient }

export type Champ = { cle: string; libelle: string; synonymes: string[] }

/** Le libellé sert d'en-tête aux fichiers modèles et aux exports (réimportables tels quels). */
export const CHAMPS: Record<TypeImport, Champ[]> = {
  articles: [
    {
      cle: 'reference',
      libelle: 'Référence',
      synonymes: ['réf', 'code', 'code article', 'référence article', 'réf fournisseur', 'référence fournisseur', 'code produit', 'sku'],
    },
    {
      cle: 'designation',
      libelle: 'Désignation',
      synonymes: ['libellé', 'description', 'article', 'produit', 'prestation', 'intitulé', 'nom', 'ouvrage', 'désignation article', 'libellé article'],
    },
    { cle: 'unite', libelle: 'Unité', synonymes: ['u', 'um', 'unité de mesure', 'unité de vente'] },
    {
      cle: 'prixAchat',
      libelle: "Prix d'achat HT",
      synonymes: ["prix d'achat", 'prix achat', 'prix achat ht', 'pa', 'pa ht', 'pu achat', 'coût', 'coût unitaire', 'coût ht', 'prix fournisseur', 'tarif fournisseur', 'prix net', 'prix net ht'],
    },
    {
      cle: 'coefficient',
      libelle: 'Coefficient de marge',
      synonymes: ['coefficient', 'coef', 'coeff', 'coef marge', 'coefficient de vente', 'coef de vente'],
    },
    {
      cle: 'prixVente',
      libelle: 'Prix de vente HT',
      synonymes: ['prix de vente', 'prix vente', 'pv', 'pv ht', 'prix ht', 'prix unitaire', 'prix unitaire ht', 'pu', 'pu ht', 'prix', 'tarif', 'tarif ht', 'prix public'],
    },
    { cle: 'tva', libelle: 'TVA (%)', synonymes: ['tva', 'taux tva', 'taux de tva', 'tx tva', 'tva %'] },
  ],
  clients: [
    {
      cle: 'nom',
      libelle: 'Nom',
      synonymes: ['client', 'nom client', 'nom du client', 'raison sociale', 'société', 'entreprise', 'nom prénom', 'nom et prénom'],
    },
    {
      cle: 'telephone',
      libelle: 'Téléphone',
      synonymes: ['tél', 'tel', 'portable', 'mobile', 'gsm', 'téléphone portable', 'numéro de téléphone', 'tél portable'],
    },
    { cle: 'email', libelle: 'E-mail', synonymes: ['mail', 'email', 'courriel', 'e mail', 'adresse mail', 'adresse e mail', 'adresse email', 'adresse courriel', 'mail client'] },
    { cle: 'adresse', libelle: 'Adresse', synonymes: ['adresse postale', 'adresse client', 'rue', 'voie', 'adresse ligne 1', 'adresse 1', 'adresse de facturation'] },
    { cle: 'codePostal', libelle: 'Code postal', synonymes: ['cp', 'c p', 'code postale', 'zip'] },
    { cle: 'ville', libelle: 'Ville', synonymes: ['commune', 'localité'] },
    { cle: 'siret', libelle: 'SIRET', synonymes: ['n siret', 'numéro siret', 'siret client', 'n° siret'] },
  ],
}

/** Taux de TVA acceptés à l'import (0 : autoliquidation, 2,1 : cas particuliers). */
export const TAUX_TVA_IMPORT = [20, 10, 5.5, 2.1, 0]

/** Mapping : pour chaque colonne du fichier, la clé du champ alimenté (ou null : colonne ignorée). */
export type Mapping = (string | null)[]

// -- En-tête et mapping ---------------------------------------------------------

/** « Prix HT (€) » → « prix ht », « Réf. » → « ref ». */
export function normaliserEntete(c: Cellule): string {
  return normaliser(String(c ?? ''))
    .replace(/\(.*?\)/g, ' ')
    .replace(/[^a-z0-9²³]+/g, ' ')
    .trim()
}

const synonymesNormalises = (champ: Champ) =>
  [champ.libelle, ...champ.synonymes].map(normaliserEntete).filter(Boolean)

/**
 * Propose un champ pour chaque colonne : égalité exacte d'abord, puis en-tête
 * contenant un synonyme (« Désignation de l'article »). Un champ n'est
 * attribué qu'à une colonne.
 */
export function proposerMapping(type: TypeImport, entetes: Cellule[]): Mapping {
  const mapping: Mapping = entetes.map(() => null)
  const pris = new Set<string>()
  const normalises = entetes.map(normaliserEntete)

  normalises.forEach((e, i) => {
    const champ = CHAMPS[type].find((c) => !pris.has(c.cle) && synonymesNormalises(c).includes(e))
    if (e && champ) {
      mapping[i] = champ.cle
      pris.add(champ.cle)
    }
  })
  normalises.forEach((e, i) => {
    if (!e || mapping[i]) return
    const mots = ` ${e} `
    const champ = CHAMPS[type].find(
      (c) => !pris.has(c.cle) && synonymesNormalises(c).some((s) => s.length >= 3 && mots.includes(` ${s} `)),
    )
    if (champ) {
      mapping[i] = champ.cle
      pris.add(champ.cle)
    }
  })
  return mapping
}

/** Ligne d'en-tête : parmi les 10 premières, celle qui reconnaît le plus de champs. */
export function detecterEntete(type: TypeImport, lignes: Cellule[][]): number {
  let meilleure = -1
  let score = 0
  lignes.slice(0, 10).forEach((l, i) => {
    const s = proposerMapping(type, l).filter(Boolean).length
    if (s > score) {
      score = s
      meilleure = i
    }
  })
  return meilleure >= 0 ? meilleure : Math.max(0, lignes.findIndex((l) => l.some((c) => c !== null)))
}

/** Champs indispensables non associés à une colonne (l'import n'est pas possible). */
export function champsManquants(type: TypeImport, mapping: Mapping): string[] {
  const a = (cle: string) => mapping.includes(cle)
  if (type === 'clients') return a('nom') ? [] : ['Nom']
  return [...(a('designation') ? [] : ['Désignation']), ...(a('prixVente') || a('prixAchat') ? [] : ['Prix de vente ou prix d’achat'])]
}

// -- Lecture des valeurs ------------------------------------------------------------

const texte = (c: Cellule | undefined) => {
  const s = c === null || c === undefined ? '' : String(c).trim()
  return s || undefined
}

/**
 * Nombre d'une cellule : nombre Excel, ou texte à la française (« 1 234,50 € »,
 * « 1.234,50 », « 20 % »). undefined : cellule vide ; null : illisible.
 */
export function lireDecimal(c: Cellule | undefined): Decimal | null | undefined {
  if (c === null || c === undefined) return undefined
  if (typeof c === 'number') return Number.isFinite(c) ? new Decimal(c) : null
  if (typeof c === 'boolean') return null
  let s = c.replace(/[\s€%]/g, '')
  if (s === '') return undefined
  // Les deux séparateurs : le dernier est la virgule décimale, l'autre sépare les milliers
  if (s.includes(',') && s.includes('.')) {
    s = s.lastIndexOf(',') > s.lastIndexOf('.') ? s.replace(/\./g, '') : s.replace(/,/g, '')
  }
  return lireNombre(s)
}

function montant(c: Cellule | undefined, libelle: string, erreurs: string[]): number | undefined {
  const d = lireDecimal(c)
  if (d === undefined) return undefined
  if (d === null) erreurs.push(`${libelle} non numérique : « ${c} »`)
  else if (d.isNegative()) erreurs.push(`${libelle} négatif`)
  else return d.times(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber()
  return undefined
}

/** Excel perd le 0 initial d'un numéro saisi comme nombre : 601020304 → 0601020304. */
export function lireTelephone(c: Cellule | undefined): string | undefined {
  const s = typeof c === 'number' && Number.isInteger(c) ? String(c) : texte(c)
  return s && /^[1-9]\d{8}$/.test(s) ? `0${s}` : s
}

// -- Contrôle des lignes ------------------------------------------------------------

export type OptionsImport = {
  tvaParDefaut: number
  /** Franchise en base de TVA (réglages entreprise) : tout article est importé à 0 %, quelle que soit la colonne TVA. */
  franchise?: boolean
}

export type LigneAnalysee<C> = {
  numero: number // numéro de ligne dans le fichier
  libelle: string
  fourni: Partial<C> // champs renseignés dans le fichier : seuls à changer lors d'une mise à jour
  complet: C | null // contenu à créer (valeurs par défaut comprises) ; null si la ligne est en erreur
  erreurs: string[]
  avertissements: string[]
}

type Valeurs = Partial<Record<string, Cellule>>

function analyserArticle(v: Valeurs, options: OptionsImport) {
  const erreurs: string[] = []
  const avertissements: string[] = []
  const fourni: Partial<ContenuArticle> = {}

  const designation = texte(v.designation)
  if (designation) fourni.designation = designation
  else erreurs.push('Désignation absente')
  const reference = texte(v.reference)
  if (reference) fourni.reference = reference

  const unite = texte(v.unite)
  if (unite) {
    const reconnue = normaliserUnite(unite)
    fourni.unite = reconnue ?? unite
    if (!reconnue) avertissements.push(`Unité inconnue : « ${unite} », conservée`)
  }

  const nbErreurs = erreurs.length
  const achat = montant(v.prixAchat, "Prix d'achat", erreurs)
  if (achat !== undefined) fourni.prixAchatHtCents = achat

  const coef = lireDecimal(v.coefficient)
  if (coef === null) erreurs.push(`Coefficient non numérique : « ${v.coefficient} »`)
  else if (coef && (!coef.gt(0) || coef.gte(1000))) erreurs.push('Coefficient hors limites (de 0 à 999)')
  else if (coef) fourni.coefficientMarge = coef.toDecimalPlaces(3, Decimal.ROUND_HALF_UP).toNumber()

  const vente = montant(v.prixVente, 'Prix de vente', erreurs)
  if (vente !== undefined) fourni.prixVenteHtCents = vente
  else if (achat !== undefined && fourni.coefficientMarge !== undefined) {
    fourni.prixVenteHtCents = prixVenteDepuisAchat(achat, fourni.coefficientMarge)
  } else if (erreurs.length === nbErreurs) {
    erreurs.push(achat !== undefined ? 'Prix de vente absent (ni coefficient de marge)' : 'Prix absent')
  }

  const nbErreursTva = erreurs.length
  const tva = lireDecimal(v.tva)
  if (tva === null) erreurs.push(`TVA non numérique : « ${v.tva} »`)
  else if (tva === undefined) {
    if (!options.franchise) avertissements.push(`TVA absente : ${String(options.tvaParDefaut).replace('.', ',')} % appliqués`)
  } else {
    // Cellule Excel au format pourcentage : 20 % est stocké 0,2
    const taux = tva.gt(0) && tva.lt(1) ? tva.times(100) : tva
    if (TAUX_TVA_IMPORT.some((t) => taux.eq(t))) fourni.tvaPercent = taux.toNumber()
    else erreurs.push(`TVA inconnue : ${taux.toString().replace('.', ',')} %`)
  }
  if (options.franchise && erreurs.length === nbErreursTva) {
    // Franchise : 0 % imposé, y compris à une mise à jour (le patch ne contient que les champs fournis)
    if (fourni.tvaPercent !== undefined && fourni.tvaPercent !== 0) {
      avertissements.push(`TVA ${String(fourni.tvaPercent).replace('.', ',')} % remplacée par 0 % (franchise en base de TVA)`)
    }
    fourni.tvaPercent = 0
  }

  if (!fourni.unite) avertissements.push('Unité absente : « u » appliquée')

  const complet: ContenuArticle | null =
    erreurs.length > 0
      ? null
      : {
          designation: fourni.designation!,
          ...(fourni.reference && { reference: fourni.reference }),
          unite: fourni.unite ?? 'u',
          ...(fourni.prixAchatHtCents !== undefined && { prixAchatHtCents: fourni.prixAchatHtCents }),
          ...(fourni.coefficientMarge !== undefined && { coefficientMarge: fourni.coefficientMarge }),
          prixVenteHtCents: fourni.prixVenteHtCents!,
          tvaPercent: fourni.tvaPercent ?? options.tvaParDefaut,
        }
  return { libelle: designation ?? reference ?? '', fourni, complet, erreurs, avertissements }
}

/** Un nombre Excel perd ses zéros de tête et peut porter des espaces : on rend le texte de chiffres. */
const chiffresDe = (c: Cellule | undefined) => {
  const s = typeof c === 'number' && Number.isInteger(c) ? String(c) : texte(c)
  return s?.replace(/[\s.]/g, '')
}

/** Code postal sur 5 chiffres (01000 saisi 1000 par Excel) ; undefined : vide ; null : illisible. */
export function lireCodePostal(c: Cellule | undefined): string | null | undefined {
  const s = chiffresDe(c)
  if (s === undefined) return undefined
  if (/^\d{4}$/.test(s) && typeof c === 'number') return `0${s}`
  return /^\d{5}$/.test(s) ? s : null
}

function analyserClient(v: Valeurs) {
  const erreurs: string[] = []
  const fourni: Partial<ContenuClient> = {}
  const nom = texte(v.nom)
  if (nom) fourni.nom = nom
  else erreurs.push('Nom absent')
  const telephone = lireTelephone(v.telephone)
  if (telephone) fourni.telephone = telephone
  const email = texte(v.email)?.toLowerCase()
  if (email && EMAIL.test(email)) fourni.email = email
  else if (email) erreurs.push(`E-mail invalide : « ${v.email} »`)
  const adresse = texte(v.adresse)
  if (adresse) fourni.adresse = adresse
  const ville = texte(v.ville)
  if (ville) fourni.ville = ville
  const codePostal = lireCodePostal(v.codePostal)
  if (codePostal) fourni.codePostal = codePostal
  else if (codePostal === null) erreurs.push(`Code postal invalide : « ${v.codePostal} » (5 chiffres)`)
  const siret = chiffresDe(v.siret)
  if (siret && /^\d{14}$/.test(siret)) fourni.siret = siret
  else if (siret) erreurs.push(`SIRET invalide : « ${v.siret} » (14 chiffres)`)
  const complet: ContenuClient | null =
    erreurs.length > 0 ? null : { nom: nom!, ...(telephone && { telephone }), ...(fourni.email && { email: fourni.email }), ...(adresse && { adresse }), ...(codePostal && { codePostal }), ...(ville && { ville }), ...(fourni.siret && { siret: fourni.siret }) }
  return { libelle: nom ?? telephone ?? '', fourni, complet, erreurs, avertissements: [] as string[] }
}

/** Contrôle chaque ligne sous l'en-tête ; les lignes vides (colonnes retenues) sont ignorées. */
export function analyser<T extends TypeImport>(
  type: T,
  feuille: Feuille,
  ligneEntete: number,
  mapping: Mapping,
  options: OptionsImport,
): LigneAnalysee<Contenus[T]>[] {
  const resultat: LigneAnalysee<Contenus[T]>[] = []
  feuille.lignes.forEach((ligne, i) => {
    if (i <= ligneEntete) return
    const valeurs: Valeurs = {}
    mapping.forEach((cle, col) => {
      if (cle && ligne[col] !== null && ligne[col] !== undefined) valeurs[cle] = ligne[col]
    })
    if (Object.values(valeurs).every((c) => texte(c) === undefined)) return
    const analyse = type === 'articles' ? analyserArticle(valeurs, options) : analyserClient(valeurs)
    resultat.push({ numero: feuille.premiereLigne + i, ...(analyse as Omit<LigneAnalysee<Contenus[T]>, 'numero'>) })
  })
  return resultat
}

// -- Doublons --------------------------------------------------------------------------

type Index<C> = { chercher(c: Partial<C>): string | undefined; ajouter(id: string, c: Partial<C>): void }

/** Même référence ; à défaut, même désignation (sans accents ni casse) et pas de référence différente. */
function indexArticles(): Index<ContenuArticle> {
  const parReference = new Map<string, string>()
  const parDesignation = new Map<string, { id: string; reference?: string }[]>()
  const n = (s: string) => normaliser(s).replace(/\s+/g, ' ').trim()
  return {
    chercher(a) {
      const memeDesignation = a.designation ? (parDesignation.get(n(a.designation)) ?? []) : []
      if (a.reference) {
        return parReference.get(n(a.reference)) ?? memeDesignation.find((x) => !x.reference)?.id
      }
      return memeDesignation[0]?.id
    },
    ajouter(id, a) {
      if (a.reference && !parReference.has(n(a.reference))) parReference.set(n(a.reference), id)
      if (a.designation) {
        const cle = n(a.designation)
        parDesignation.set(cle, [...(parDesignation.get(cle) ?? []), { id, reference: a.reference && n(a.reference) }])
      }
    },
  }
}

/** Même nom (sans accents ni casse), sauf si les deux ont un téléphone ou un SIRET et qu'ils diffèrent. */
function indexClients(): Index<ContenuClient> {
  const parNom = new Map<string, { id: string; telephone?: string; siret?: string }[]>()
  const n = (s: string) => normaliser(s).replace(/\s+/g, ' ').trim()
  const chiffres = (t?: string) => t?.replace(/\D/g, '') || undefined
  return {
    chercher(c) {
      const tel = chiffres(c.telephone)
      return (c.nom ? (parNom.get(n(c.nom)) ?? []) : []).find((x) => (!x.telephone || !tel || x.telephone === tel) && (!x.siret || !c.siret || x.siret === c.siret))?.id
    },
    ajouter(id, c) {
      if (!c.nom) return
      const cle = n(c.nom)
      parNom.set(cle, [...(parNom.get(cle) ?? []), { id, telephone: chiffres(c.telephone), siret: c.siret }])
    },
  }
}

// -- Plan d'import ----------------------------------------------------------------------

export type ModeDoublons = 'ignorer' | 'mettre_a_jour' | 'creer'

export type Action<C> = { type: 'creer'; id: string; contenu: C } | { type: 'modifier'; id: string; patch: Partial<C> }

export type ResultatLigne = {
  numero: number
  libelle: string
  resultat: 'cree' | 'mis_a_jour' | 'ignore' | 'erreur'
  message: string
}

export type PlanImport<C> = { actions: Action<C>[]; resultats: ResultatLigne[] }

// Libellés et mise en forme des champs, pour montrer ce qu'une mise à jour change (avant → après)
const LIBELLES_CHAMP: Record<string, string> = {
  reference: 'Référence', designation: 'Désignation', unite: 'Unité', prixAchatHtCents: 'Prix d’achat HT', coefficientMarge: 'Coefficient',
  prixVenteHtCents: 'Prix de vente HT', tvaPercent: 'TVA', nom: 'Nom', telephone: 'Téléphone', email: 'E-mail', adresse: 'Adresse',
  codePostal: 'Code postal', ville: 'Ville', siret: 'SIRET', adresseElectronique: 'Adresse électronique',
}
const CHAMPS_PRIX = new Set(['prixAchatHtCents', 'prixVenteHtCents'])
const montrer = (cle: string, v: unknown) => {
  if (v === undefined || v === '' || v === null) return '—'
  if (CHAMPS_PRIX.has(cle) && typeof v === 'number') return formatEuro(centimesVersEuros(v)).replace(/[\u00a0\u202f]/g, ' ')
  if (cle === 'tvaPercent') return `${String(v).replace('.', ',')} %`
  return String(v).replace('.', cle === 'coefficientMarge' ? ',' : '.')
}

/** Variation en % d'un prix (une décimale, signe explicite) ; null si l'un des prix manque ou est nul. */
export function variationPrix(avant: unknown, apres: unknown): number | null {
  if (typeof avant !== 'number' || typeof apres !== 'number' || avant <= 0) return null
  return new Decimal(apres).minus(avant).dividedBy(avant).times(100).toDecimalPlaces(1).toNumber()
}
const signe = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${String(Math.abs(n)).replace('.', ',')} %`

/** Ce qu'une mise à jour change, champ par champ : « Prix de vente HT 45,00 € → 48,50 € (+7,8 %) ». */
export function decrireChangements(actuel: object, patch: object): string {
  const a = actuel as Record<string, unknown>
  const morceaux = Object.entries(patch).map(([cle, apres]) => {
    const v = variationPrix(a[cle], apres)
    return `${LIBELLES_CHAMP[cle] ?? cle} ${montrer(cle, a[cle])} → ${montrer(cle, apres)}${CHAMPS_PRIX.has(cle) && v !== null ? ` (${signe(v)})` : ''}`
  })
  return morceaux.length > 3 ? `${morceaux.slice(0, 3).join(' ; ')} ; et ${morceaux.length - 3} autre${morceaux.length - 3 > 1 ? 's' : ''}` : morceaux.join(' ; ')
}

export type BilanPrix = { hausses: number; baisses: number; inchanges: number; moyennePercent: number | null; /** Variations qui dépassent le seuil (en valeur absolue), les plus fortes d'abord. */ depassements: { id: string; designation: string; variation: number }[] }

/** Bilan des prix de vente touchés par un plan : nombre de hausses et de baisses, variation moyenne (articles mis à jour seulement). */
export function bilanPrix(actions: Action<ContenuArticle>[], existants: (ContenuArticle & { id: string })[], seuilPercent?: number): BilanPrix {
  const variations: number[] = []
  const depassements: BilanPrix['depassements'] = []
  let inchanges = 0
  for (const a of actions) {
    if (a.type !== 'modifier' || a.patch.prixVenteHtCents === undefined) continue
    const v = variationPrix(existants.find((e) => e.id === a.id)?.prixVenteHtCents, a.patch.prixVenteHtCents)
    if (v === null) inchanges++
    else {
      variations.push(v)
      const e = existants.find((x) => x.id === a.id)
      if (seuilPercent !== undefined && Math.abs(v) > seuilPercent && e) depassements.push({ id: a.id, designation: e.designation, variation: v })
    }
  }
  const moyenne = variations.length ? new Decimal(variations.reduce((n, v) => n + v, 0)).dividedBy(variations.length).toDecimalPlaces(1).toNumber() : null
  return { hausses: variations.filter((v) => v > 0).length, baisses: variations.filter((v) => v < 0).length, inchanges, moyennePercent: moyenne, depassements: depassements.sort((a, b) => Math.abs(b.variation) - Math.abs(a.variation)) }
}

const egal = (a: unknown, b: unknown) => a === b || (a === undefined && b === '') || (a === '' && b === undefined)

/**
 * Plan d'écriture. Un doublon (dans la base ou plus haut dans le fichier) est
 * ignoré, mis à jour (seuls les champs renseignés dans le fichier changent)
 * ou créé quand même, selon le mode choisi.
 */
export function planifier<T extends TypeImport>(
  type: T,
  lignes: LigneAnalysee<Contenus[T]>[],
  existants: (Contenus[T] & { id: string })[],
  mode: ModeDoublons,
): PlanImport<Contenus[T]> {
  type C = Contenus[T]
  const index = (type === 'articles' ? indexArticles() : indexClients()) as Index<C>
  const etat = new Map<string, C>() // état connu de chaque document (existant ou créé par cet import)
  const libelleDe = (c: C) => ('designation' in c ? c.designation : c.nom)
  for (const e of existants) {
    index.ajouter(e.id, e)
    etat.set(e.id, e)
  }

  const actions: Action<C>[] = []
  const creations = new Map<string, Action<C> & { type: 'creer' }>()
  const resultats: ResultatLigne[] = []

  for (const l of lignes) {
    const base = { numero: l.numero, libelle: l.libelle }
    if (!l.complet) {
      resultats.push({ ...base, resultat: 'erreur', message: l.erreurs.join(' ; ') })
      continue
    }
    const doublon = index.chercher(l.fourni)
    if (doublon && mode !== 'creer') {
      const actuel = etat.get(doublon)!
      const origine = creations.has(doublon) ? 'déjà présent plus haut dans le fichier' : `déjà dans la base`
      if (mode === 'ignorer') {
        resultats.push({ ...base, resultat: 'ignore', message: `Doublon de « ${libelleDe(actuel)} », ${origine}` })
        continue
      }
      const patch = Object.fromEntries(
        Object.entries(l.fourni).filter(([k, v]) => !egal((actuel as Record<string, unknown>)[k], v)),
      ) as Partial<C>
      if (Object.keys(patch).length === 0) {
        resultats.push({ ...base, resultat: 'ignore', message: `Identique à « ${libelleDe(actuel)} »` })
        continue
      }
      const suivant = { ...actuel, ...patch }
      etat.set(doublon, suivant)
      const creation = creations.get(doublon)
      if (creation) creation.contenu = suivant
      else actions.push({ type: 'modifier', id: doublon, patch })
      resultats.push({ ...base, resultat: 'mis_a_jour', message: decrireChangements(actuel, patch) })
      continue
    }
    const id = nouvelId()
    const action = { type: 'creer' as const, id, contenu: l.complet }
    actions.push(action)
    creations.set(id, action)
    index.ajouter(id, l.complet)
    etat.set(id, l.complet)
    resultats.push({ ...base, resultat: 'cree', message: l.avertissements.join(' ; ') })
  }
  return { actions, resultats }
}
